//AvatarMirror.tsx — avatar.xrscape.ai: the mirror becomes the picked character, live

import { useEffect, useState } from 'react'
import { avatarToCharacter } from '../../shared/avatarCharacter'
import type { CharacterOverride } from '../tryon/characterOverride'
import { isBusy, isLive } from '../tryon/tryOnController'
import { useTryOnSession } from '../tryon/useTryOnSession'
import { CharacterStrip } from '../ui/CharacterStrip'
import { GearIcon } from '../ui/icons'
import { MirrorVideo } from '../ui/MirrorVideo'
import { AvatarSettings } from './AvatarSettings'
import { loadSettings, saveSettings } from './storedSettings'
import { useLiveCharacters } from './useLiveCharacters'

export default function AvatarMirror() {
	const { state, controller } = useTryOnSession()
	const { characters, error: liveError } = useLiveCharacters()
	const [isShowingSettings, setIsShowingSettings] = useState(false)

	const live = isLive(state)
	const busy = isBusy(state)
	const settings = state.override
	const strip = (characters ?? []).map(avatarToCharacter)

	//The override is what puts the shared controller into character mode. Lucy's frames are shown
	//as delivered, so upscaling and the stats probe stay off even if an earlier build left them on.
	useEffect(() => {
		controller.setOverride(loadSettings())
		void controller.updatePreference({ upscaleMode: 'off', showUpscaleStats: false })
	}, [controller])

	//An admin save to the picked character reaches a running session at once. The first load, or
	//the picked character being deleted, falls back to the first one.
	useEffect(() => {
		if (!characters) return
		const picked = controller.getSnapshot().character
		const fresh = picked && characters.find((entry) => entry.id === picked.id)
		if (fresh) void controller.updateCharacter(avatarToCharacter(fresh))
		else if (characters[0]) void controller.selectCharacter(avatarToCharacter(characters[0]))
	}, [controller, characters])

	const changeSettings = (patch: Partial<CharacterOverride>) => {
		if (!settings) return
		controller.setOverride(saveSettings({ ...settings, ...patch }))
		void controller.applyOverride()
	}

	const message = state.error ?? liveError ?? state.notice
	const isProblem = Boolean(state.error ?? liveError)

	return (
		<div className="app">
			<MirrorVideo
				controller={controller}
				mirror={state.preference.mirror}
				upscaleMode="off"
				showUpscaleStats={false}
				latency={null}
				placeholder={busy ? 'Connecting…' : 'Starting camera'}
			/>

			<div className="hud">
				{/* Empty span keeps the gear pinned right: the mirror shows no status readout. */}
				<span />
				<button
					type="button"
					className={`ghost icon-only ${live ? 'is-live' : ''}`}
					onClick={() => setIsShowingSettings(true)}
					aria-label="Settings and session"
				>
					<GearIcon />
				</button>
			</div>

			{message && (
				<div className={`toast ${isProblem ? 'is-problem' : ''}`} role="status">
					<span>{message}</span>
					<button type="button" className="link" onClick={() => controller.dismissError()}>
						Dismiss
					</button>
				</div>
			)}

			{state.preference.showDock && (
				<div className="dock-area">
					{characters?.length === 0 && <p className="hint">No characters yet. Add them at /admin.</p>}
					<div className="dock">
						<CharacterStrip
							characters={strip}
							selectedId={state.character?.id ?? null}
							disabled={state.isSwitching}
							onSelect={(character) => void controller.selectCharacter(character)}
						/>
					</div>
				</div>
			)}

			{isShowingSettings && settings && (
				<AvatarSettings
					settings={settings}
					preference={state.preference}
					session={{
						live,
						busy,
						canStart: strip.length > 0,
						onToggle: () => {
							if (live) {
								void controller.stop()
								return
							}
							//Closed before starting so the reveal is the mirror, not this panel.
							setIsShowingSettings(false)
							void controller.start(undefined, strip[0])
						},
					}}
					onSettings={changeSettings}
					onPreference={(patch) => void controller.updatePreference(patch)}
					onClose={() => setIsShowingSettings(false)}
				/>
			)}
		</div>
	)
}
